import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { adminFetch } from "utils/adminFetch";
import SiteHeaderBanner from "components/SiteHeaderBanner";
import { invalidateSiteHeaderMessagesCache } from "hooks/useSiteHeaderMessages";
import MessageStyleFields from "views/admin/maintenance/MessageStyleFields";
import {
  EMPTY_STYLE_FORM,
  styleFormFromRow,
  stylePayloadFromForm,
} from "constants/siteHeaderMessageStyle";

const KIND_OPTIONS = [
  { value: "promotion", label: "Promotion" },
  { value: "maintenance", label: "Maintenance" },
  { value: "update", label: "Update date" },
  { value: "info", label: "Info" },
];

const AUDIENCE_OPTIONS = [
  { value: "all", label: "Everyone (public + clients)" },
  { value: "public", label: "Public site only" },
  { value: "client", label: "Client area only" },
];

const EMPTY_FORM = {
  kind: "promotion",
  message: "",
  updateDate: "",
  linkUrl: "",
  linkLabel: "",
  audience: "all",
  startsAt: "",
  endsAt: "",
  isActive: true,
  priority: "0",
  ...EMPTY_STYLE_FORM,
};

function toYmd(v) {
  if (!v) return "";
  const s = String(v);
  if (s.length >= 10 && s[4] === "-") return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function toDatetimeLocal(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function statusChip(row) {
  if (!row.isActive) {
    return (
      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
        Inactive
      </span>
    );
  }
  const now = Date.now();
  if (row.startsAt && new Date(row.startsAt).getTime() > now) {
    return (
      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
        Scheduled
      </span>
    );
  }
  if (row.endsAt && new Date(row.endsAt).getTime() < now) {
    return (
      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-400">
        Expired
      </span>
    );
  }
  return (
    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
      Live
    </span>
  );
}

function buildPayload(form) {
  return {
    kind: form.kind,
    message: form.message.trim(),
    updateDate: form.updateDate || null,
    linkUrl: form.linkUrl.trim() || null,
    linkLabel: form.linkLabel.trim() || null,
    audience: form.audience,
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    isActive: Boolean(form.isActive),
    priority: Number(form.priority) || 0,
    ...stylePayloadFromForm(form),
  };
}

function previewRowFromForm(form) {
  const style = stylePayloadFromForm(form);
  return {
    id: "preview",
    kind: form.kind,
    message: form.message.trim() || "Your message preview…",
    updateDate: form.updateDate || null,
    linkUrl: form.linkUrl.trim() || null,
    linkLabel: form.linkLabel.trim() || null,
    ...style,
  };
}

export default function MaintenanceAdmin() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadRows = useCallback(async () => {
    setTableLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/site-header-messages");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load messages.");
        setRows([]);
        return;
      }
      setRows(data.rows || []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  async function handleCreate(e) {
    e.preventDefault();
    setMsg("");
    setError("");
    if (!form.message.trim()) {
      setError("Message is required.");
      return;
    }
    setLoading(true);
    try {
      const res = await adminFetch("/api/admin/site-header-messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload(form)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not create message.");
        if (data.hint) setError((prev) => `${prev} ${data.hint}`);
        return;
      }
      invalidateSiteHeaderMessagesCache();
      setMsg("Message created.");
      setForm(EMPTY_FORM);
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }

  function openEdit(r) {
    setEditingRow(r);
    setEditForm({
      kind: r.kind || "info",
      message: r.message || "",
      updateDate: toYmd(r.updateDate),
      linkUrl: r.linkUrl || "",
      linkLabel: r.linkLabel || "",
      audience: r.audience || "all",
      startsAt: toDatetimeLocal(r.startsAt),
      endsAt: toDatetimeLocal(r.endsAt),
      isActive: Boolean(r.isActive),
      priority: String(r.priority ?? 0),
      ...styleFormFromRow(r),
    });
  }

  async function saveEdit() {
    if (!editingRow || !editForm) return;
    setSavingEdit(true);
    setError("");
    try {
      const res = await adminFetch(
        `/api/admin/site-header-messages/${editingRow.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPayload(editForm)),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Update failed.");
        return;
      }
      invalidateSiteHeaderMessagesCache();
      setEditingRow(null);
      setEditForm(null);
      setMsg("Message updated.");
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    setError("");
    try {
      const res = await adminFetch(
        `/api/admin/site-header-messages/${deleteTarget.id}`,
        { method: "DELETE" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Delete failed.");
        return;
      }
      invalidateSiteHeaderMessagesCache();
      setDeleteTarget(null);
      setMsg("Message deleted.");
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setDeletingId(null);
    }
  }

  const previewMessages = [previewRowFromForm(form)];

  return (
    <div className="mt-3 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          Maintenance
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Header banners on the public site and client area — promotions,
          maintenance notices, and update dates.
        </p>
      </div>

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
          {error}
          {error.includes("site_header_messages") ? (
            <span className="mt-2 block">
              Run{" "}
              <code className="rounded bg-white/60 px-1 dark:bg-black/30">
                backend/sql/site_header_messages.sql
              </code>{" "}
              and{" "}
              <code className="rounded bg-white/60 px-1 dark:bg-black/30">
                backend/sql/site_header_messages_style.sql
              </code>{" "}
              in Supabase SQL Editor.
            </span>
          ) : null}
        </p>
      ) : null}

      {msg ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200">
          {msg}
        </p>
      ) : null}

      <form
        onSubmit={handleCreate}
        className="space-y-4 rounded-[20px] border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800"
      >
        <h2 className="text-lg font-bold text-navy-700 dark:text-white">
          New header message
        </h2>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Type
            </span>
            <select
              value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Audience
            </span>
            <select
              value={form.audience}
              onChange={(e) =>
                setForm((f) => ({ ...f, audience: e.target.value }))
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            >
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
            Message
          </span>
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            rows={3}
            placeholder="e.g. 20% off all plans until June 30 — use code SUMMER20"
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            required
          />
        </label>

        <div className="grid gap-4 md:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Update date (optional)
            </span>
            <input
              type="date"
              value={form.updateDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, updateDate: e.target.value }))
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Shown in the banner for maintenance / updates.
            </span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Show from (optional)
            </span>
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, startsAt: e.target.value }))
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Hide after (optional)
            </span>
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, endsAt: e.target.value }))
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Link URL (optional)
            </span>
            <input
              type="text"
              value={form.linkUrl}
              onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
              placeholder="/Pricing or https://…"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Link label
            </span>
            <input
              type="text"
              value={form.linkLabel}
              onChange={(e) =>
                setForm((f) => ({ ...f, linkLabel: e.target.value }))
              }
              placeholder="Learn more"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
              Priority
            </span>
            <input
              type="number"
              value={form.priority}
              onChange={(e) =>
                setForm((f) => ({ ...f, priority: e.target.value }))
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Higher = shown first.
            </span>
          </label>
        </div>

        <MessageStyleFields
          style={form}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        />

        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) =>
              setForm((f) => ({ ...f, isActive: e.target.checked }))
            }
          />
          Active (visible when scheduled window allows)
        </label>

        <button
          type="submit"
          disabled={loading}
          className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? "Saving…" : "Add message"}
        </button>
      </form>

      {form.message.trim() || form.barColor || form.textColor ? (
        <div className="overflow-hidden rounded-[20px] border border-gray-200 dark:border-white/10">
          <p className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-navy-900 dark:text-gray-400">
            Live preview (top of site)
          </p>
          <SiteHeaderBanner messages={previewMessages} />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-[20px] border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <h2 className="font-bold text-navy-700 dark:text-white">All messages</h2>
          <button
            type="button"
            onClick={loadRows}
            disabled={tableLoading}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
          >
            {tableLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10">
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3">Update date</th>
              <th className="px-4 py-3">Audience</th>
              <th className="px-4 py-3">Schedule</th>
              <th className="px-4 py-3">Style</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No messages yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 align-top dark:border-white/5"
                >
                  <td className="px-4 py-3">{statusChip(row)}</td>
                  <td className="px-4 py-3 capitalize text-gray-700 dark:text-gray-300">
                    {row.kind}
                  </td>
                  <td className="max-w-xs px-4 py-3 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {row.message}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                    {toYmd(row.updateDate) || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {row.audience}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    <div>From: {formatWhen(row.startsAt)}</div>
                    <div>To: {formatWhen(row.endsAt)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    <div>
                      Bar: {row.barColor || "default"}
                    </div>
                    <div>
                      Text: {row.textColor || "default"}
                      {row.fontItalic ? ", italic" : ""}
                    </div>
                    <div>
                      {row.fontFamily || "default font"},{" "}
                      {row.fontSizePx ? `${row.fontSizePx}px` : "14px"},{" "}
                      {row.textAlign || "center"}
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.priority}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-bold text-white hover:bg-brand-600"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                        className="rounded-lg border border-red-200 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-50 dark:border-red-500/30 dark:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingRow && editForm
        ? createPortal(
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-navy-800">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                  Edit message
                </h3>
                <div className="mt-4 space-y-3">
                  <select
                    value={editForm.kind}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, kind: e.target.value }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  >
                    {KIND_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={editForm.message}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, message: e.target.value }))
                    }
                    rows={3}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                  <input
                    type="date"
                    value={editForm.updateDate}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, updateDate: e.target.value }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                  <select
                    value={editForm.audience}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, audience: e.target.value }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  >
                    {AUDIENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <MessageStyleFields
                    style={editForm}
                    onChange={(patch) =>
                      setEditForm((f) => ({ ...f, ...patch }))
                    }
                  />
                  <SiteHeaderBanner messages={[previewRowFromForm(editForm)]} />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                    Active
                  </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRow(null);
                      setEditForm(null);
                    }}
                    className="rounded-xl border px-4 py-2 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={savingEdit}
                    className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {savingEdit ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {deleteTarget
        ? createPortal(
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-navy-800">
                <p className="text-navy-700 dark:text-white">
                  Delete this header message?
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {deleteTarget.message}
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(null)}
                    className="rounded-xl border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={deletingId === deleteTarget.id}
                    className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {deletingId === deleteTarget.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
